import { useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { FileText, Download, Eye, Trash2, Calendar as CalendarIcon, User, Plus, Pencil, FileSpreadsheet, Image as ImageIcon, Grid, List, RefreshCw, StickyNote, Send } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { format } from "date-fns";
import { useToast } from "@/hooks/use-toast";
import { useState, useEffect } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { DocumentUpload } from "./DocumentUpload";
import { ImageGallery } from "./ImageGallery";
import { DocumentViewerDialog, type ViewableDocument } from "./DocumentViewerDialog";
import { DatePickerField } from "./DatePickerField";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { DOCUMENT_TYPES, normalizeDocumentType } from "@/lib/documentTypes";
import { Checkbox } from "@/components/ui/checkbox";
import { ShareDocumentsDialog } from "./ShareDocumentsDialog";



const TRAVEL_DOC_CATEGORIES = [
  "Flight Ticket",
  "Accommodation Confirmation",
  "Voucher",
  "Visa / Permit",
  "Visa Letter",
  "Insurance",
  "Ticket",
  "Receipt",
  "Invoice",
  "Quote",
  "Programme",
  "Itinerary",
  "Contract / Agreement",
  "Correspondence",
  "Other",
];



const getFileIcon = (fileType: string, fileName: string) => {
  const ext = fileName.split('.').pop()?.toLowerCase();
  
  // Images
  if (fileType?.startsWith('image/') || ['jpg', 'jpeg', 'png', 'webp', 'gif'].includes(ext || '')) {
    return <ImageIcon className="h-5 w-5 text-blue-500" />;
  }
  
  // Spreadsheets
  if (
    fileType?.includes('spreadsheet') ||
    fileType?.includes('excel') ||
    ['xlsx', 'xls', 'xlsm', 'csv', 'numbers'].includes(ext || '')
  ) {
    return <FileSpreadsheet className="h-5 w-5 text-green-500" />;
  }
  
  // Default to text/document icon
  return <FileText className="h-5 w-5 text-primary" />;
};

const getFileTypeLabel = (fileType: string, fileName: string) => {
  const ext = fileName.split('.').pop()?.toLowerCase();
  
  if (fileType?.startsWith('image/')) return 'Image';
  if (fileType?.includes('pdf')) return 'PDF';
  if (fileType?.includes('spreadsheet') || ['xlsx', 'xls', 'xlsm'].includes(ext || '')) return 'Excel Spreadsheet';
  if (ext === 'csv') return 'CSV File';
  if (ext === 'numbers') return 'Numbers Spreadsheet';
  if (fileType?.includes('word') || ['doc', 'docx'].includes(ext || '')) return 'Word Document';
  if (fileType === 'text/plain') return 'Text Document';
  
  return 'Document';
};

const isImageFile = (fileType: string, fileName: string) => {
  const ext = fileName.split('.').pop()?.toLowerCase();
  return fileType?.startsWith('image/') || ['jpg', 'jpeg', 'png', 'webp', 'gif'].includes(ext || '');
};

const isSpreadsheetFile = (fileType: string, fileName: string) => {
  const ext = fileName.split('.').pop()?.toLowerCase();
  return fileType?.includes('spreadsheet') || fileType?.includes('excel') || ['xlsx', 'xls', 'xlsm', 'csv', 'numbers'].includes(ext || '');
};

interface DocumentVaultProps {
  leadId: string;
  itineraryItemId?: string;
}

export const DocumentVault = ({ leadId, itineraryItemId }: DocumentVaultProps) => {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const [uploadDialogOpen, setUploadDialogOpen] = useState(false);
  const [viewDialogOpen, setViewDialogOpen] = useState(false);
  const [textDocDialogOpen, setTextDocDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [viewMode, setViewMode] = useState<'list' | 'gallery'>('list');
  const [currentDocument, setCurrentDocument] = useState<ViewableDocument | null>(null);
  const [textDocTitle, setTextDocTitle] = useState("");
  const [textDocContent, setTextDocContent] = useState("");
  const [editingDocument, setEditingDocument] = useState<any>(null);
  const [renamingId, setRenamingId] = useState<string | null>(null);
  const [renameValue, setRenameValue] = useState("");
  const [rescanningId, setRescanningId] = useState<string | null>(null);
  const [shareDialogOpen, setShareDialogOpen] = useState(false);
  const [shareInitialIds, setShareInitialIds] = useState<string[]>([]);
  const [selectedForShare, setSelectedForShare] = useState<string[]>([]);

  const openShareDialog = (ids: string[]) => {
    setShareInitialIds(ids);
    setShareDialogOpen(true);
  };

  // Active external shares per document (for the badge on each card)
  const { data: externalShareCounts } = useQuery({
    queryKey: ["document-external-share-counts", leadId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("document_share_links")
        .select("id, expires_at, revoked_at, document_share_link_items(document_id)")
        .eq("lead_id", leadId)
        .is("revoked_at", null)
        .gt("expires_at", new Date().toISOString());
      if (error) throw error;
      const map: Record<string, number> = {};
      (data || []).forEach((share: any) => {
        (share.document_share_link_items || []).forEach((item: any) => {
          map[item.document_id] = (map[item.document_id] || 0) + 1;
        });
      });
      return map;
    },
  });

  const [editForm, setEditForm] = useState({
    ai_summary: "",
    extracted_vendor: "",
    extracted_document_type: "",
    extracted_date: "",
  });

  // Debug user role on mount
  useEffect(() => {
    const checkUserRole = async () => {
      const { data: { user } } = await supabase.auth.getUser();
      const { data: roles } = await supabase
        .from('user_roles')
        .select('role')
        .eq('user_id', user?.id);
      
      console.log('👤 Current user:', user?.email, 'Roles:', roles);
    };
    
    checkUserRole();
  }, []);

  const { data: suppliers } = useQuery({
    queryKey: ["suppliers-all"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("suppliers")
        .select("id, name")
        .order("name");
      if (error) throw error;
      return data;
    },
  });

  // Query notes that have document_id linking to documents in this lead
  const { data: notesByDocId } = useQuery({
    queryKey: ["notes-by-lead-docs", leadId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("notes")
        .select("id, document_id")
        .eq("lead_id", leadId)
        .not("document_id", "is", null);
      if (error) throw error;
      // Return a map of document_id -> note_id
      const map: Record<string, string> = {};
      data?.forEach((n: any) => { if (n.document_id) map[n.document_id] = n.id; });
      return map;
    },
  });

  const { data: documents, isLoading, error, refetch } = useQuery({
    queryKey: ['documents', leadId, itineraryItemId],
    queryFn: async () => {
      console.log('🔍 Fetching documents for lead:', leadId, 'itinerary item:', itineraryItemId);
      
    let query = supabase
      .from('documents')
      .select(`
        *,
        profiles!documents_user_id_fkey (full_name, email)
      `)
      .eq('lead_id', leadId);

      if (itineraryItemId) {
        query = query.eq('itinerary_item_id', itineraryItemId);
      }

      const { data, error } = await query.order('uploaded_at', { ascending: false });
      
      console.log('📊 Query result:', { 
        data, 
        error, 
        count: data?.length,
        leadId,
        hasProfiles: data?.[0]?.profiles 
      });
      
      if (error) {
        console.error('❌ Query error:', error);
        throw error;
      }
      
      return data;
    },
  });

  if (error) {
    console.error('React Query error:', error);
    return (
      <Card className="p-6">
        <div className="text-center py-4 text-destructive">
          <p className="font-semibold">Error loading documents</p>
          <p className="text-sm mt-1">{error.message}</p>
          <Button 
            onClick={() => refetch()} 
            className="mt-4"
            variant="outline"
          >
            <RefreshCw className="h-4 w-4 mr-2" />
            Retry
          </Button>
        </div>
      </Card>
    );
  }

  const downloadDocument = async (filePath: string, fileName: string) => {
    try {
      const { data, error } = await supabase.storage
        .from('documents')
        .download(filePath);

      if (error) throw error;

      const url = URL.createObjectURL(data);
      const a = document.createElement('a');
      a.href = url;
      a.download = fileName;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);

      toast({
        title: "Download started",
        description: fileName,
      });
    } catch (error: any) {
      toast({
        title: "Download failed",
        description: error.message,
        variant: "destructive",
      });
    }
  };

  const deleteDocument = async (id: string, filePath: string) => {
    try {
      // Delete from storage
      const { error: storageError } = await supabase.storage
        .from('documents')
        .remove([filePath]);

      if (storageError) throw storageError;

      // Delete record
      const { error: dbError } = await supabase
        .from('documents')
        .delete()
        .eq('id', id);

      if (dbError) throw dbError;

      toast({
        title: "Document deleted",
        description: "Document has been removed from the vault",
      });
      refetch();
    } catch (error: any) {
      toast({
        title: "Delete failed",
        description: error.message,
        variant: "destructive",
      });
    }
  };

  const viewDocument = async (filePath: string, fileName: string, fileType: string) => {
    try {
      // Prefer a signed URL: it works for in-app rendering AND for the Office
      // web viewer, which must be able to fetch the file itself.
      const { data, error } = await supabase.storage
        .from('documents')
        .createSignedUrl(filePath, 3600);

      let url: string;
      if (error || !data?.signedUrl) {
        const { data: blob, error: dlError } = await supabase.storage
          .from('documents')
          .download(filePath);
        if (dlError) throw dlError;
        url = URL.createObjectURL(blob);
      } else {
        url = data.signedUrl;
      }

      setCurrentDocument({ url, fileName, fileType });
      setViewDialogOpen(true);
    } catch (error: any) {
      toast({
        title: "View failed",
        description: error.message,
        variant: "destructive",
      });
    }
  };

  const openEditDialog = (doc: any) => {
    setEditingDocument(doc);
    setEditForm({
      ai_summary: doc.ai_summary || "",
      extracted_vendor: doc.extracted_vendor || "",
      extracted_document_type: normalizeDocumentType(doc.extracted_document_type),
      extracted_date: doc.extracted_date || "",
    });
    setEditDialogOpen(true);
  };

  const rescanDocument = async (doc: any) => {
    setRescanningId(doc.id);
    try {
      const { data, error } = await supabase.functions.invoke("analyze-document", {
        body: { documentId: doc.id },
      });
      if (error) throw error;
      if (data?.unsupported) {
        toast({ title: "Can't scan this file", description: data.reason });
      } else {
        toast({ title: "Document scanned", description: doc.file_name });
      }
      refetch();
    } catch (error: any) {
      toast({
        title: "AI scan failed",
        description: error.message || "Please fill in the details manually.",
        variant: "destructive",
      });
    } finally {
      setRescanningId(null);
    }
  };


  const toggleShareWithClient = async (doc: any, shared: boolean) => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      const { error } = await supabase
        .from("documents")
        .update({
          shared_with_client: shared,
          shared_at: shared ? new Date().toISOString() : null,
          shared_by: shared ? user?.id ?? null : null,
          travel_doc_category: shared ? (doc.travel_doc_category || "Other") : doc.travel_doc_category,
        })
        .eq("id", doc.id);

      if (error) throw error;

      toast({
        title: shared ? "Shared with client" : "Removed from client dashboard",
        description: doc.file_name,
      });
      refetch();
    } catch (error: any) {
      toast({
        title: "Could not update sharing",
        description: error.message,
        variant: "destructive",
      });
    }
  };

  const updateTravelCategory = async (doc: any, category: string) => {
    try {
      const { error } = await supabase
        .from("documents")
        .update({ travel_doc_category: category })
        .eq("id", doc.id);
      if (error) throw error;
      refetch();
    } catch (error: any) {
      toast({
        title: "Could not update category",
        description: error.message,
        variant: "destructive",
      });
    }
  };

  const saveDisplayName = async (doc: any) => {
    const value = renameValue.trim();
    try {
      const { error } = await supabase
        .from("documents")
        .update({ display_name: value || null })
        .eq("id", doc.id);
      if (error) throw error;
      setRenamingId(null);
      refetch();
    } catch (error: any) {
      toast({
        title: "Could not rename document",
        description: error.message,
        variant: "destructive",
      });
    }
  };

  const openEditDialogLegacy = (doc: any) => {
    setEditingDocument(doc);
    setEditForm({
      ai_summary: doc.ai_summary || "",
      extracted_vendor: doc.extracted_vendor || "",
      extracted_document_type: normalizeDocumentType(doc.extracted_document_type),
      extracted_date: doc.extracted_date || "",
    });
    setEditDialogOpen(true);
  };

  const updateDocument = async () => {
    if (!editingDocument) return;

    try {
      const { error } = await supabase
        .from('documents')
        .update({
          ai_summary: editForm.ai_summary || null,
          extracted_vendor: editForm.extracted_vendor || null,
          extracted_document_type: editForm.extracted_document_type || null,
          extracted_date: editForm.extracted_date || null,
        })
        .eq('id', editingDocument.id);

      if (error) throw error;

      toast({
        title: "Document updated",
        description: "Document details have been updated successfully",
      });

      setEditDialogOpen(false);
      setEditingDocument(null);
      refetch();
    } catch (error: any) {
      toast({
        title: "Update failed",
        description: error.message,
        variant: "destructive",
      });
    }
  };

  const createTextDocument = async () => {
    if (!textDocTitle.trim() || !textDocContent.trim()) {
      toast({
        title: "Missing information",
        description: "Please provide both a title and content",
        variant: "destructive",
      });
      return;
    }

    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error("User not authenticated");

      const fileName = `${textDocTitle.trim().replace(/[^a-z0-9]/gi, '_')}.txt`;
      const filePath = `${user.id}/${crypto.randomUUID()}.txt`;
      const blob = new Blob([textDocContent], { type: 'text/plain' });

      // Upload to storage
      const { error: uploadError } = await supabase.storage
        .from('documents')
        .upload(filePath, blob);

      if (uploadError) throw uploadError;

      // Create document record
      const { error: dbError } = await supabase
        .from('documents')
        .insert({
          lead_id: leadId,
          itinerary_item_id: itineraryItemId || null,
          user_id: user.id,
          file_name: fileName,
          file_path: filePath,
          file_type: 'text/plain',
          file_size: blob.size,
          notes: 'Text document created by user',
        });

      if (dbError) throw dbError;

      toast({
        title: "Text document created",
        description: fileName,
      });

      setTextDocDialogOpen(false);
      setTextDocTitle("");
      setTextDocContent("");
      refetch();
    } catch (error: any) {
      toast({
        title: "Failed to create document",
        description: error.message,
        variant: "destructive",
      });
    }
  };

  if (isLoading) {
    return <div className="text-center py-4">Loading documents...</div>;
  }

  // Separate images from other documents
  const imageDocuments = documents?.filter((doc: any) => isImageFile(doc.file_type, doc.file_name)) || [];
  const otherDocuments = documents?.filter((doc: any) => !isImageFile(doc.file_type, doc.file_name)) || [];
  const hasImages = imageDocuments.length > 0;

  return (
    <div className="space-y-4">
      {/* Debug info */}
      <div className="text-xs text-muted-foreground bg-muted/50 p-2 rounded">
        Lead ID: {leadId} | Documents: {documents?.length || 0}
      </div>

      <div className="flex flex-wrap justify-between items-center gap-2">
        <h3 className="text-lg font-semibold">Document Vault</h3>
        <div className="flex flex-wrap gap-2">
          <Button 
            onClick={() => {
              queryClient.invalidateQueries({ queryKey: ['documents'] });
              refetch();
            }} 
            size="sm" 
            variant="ghost"
            title="Refresh document list"
          >
            <RefreshCw className="h-4 w-4" />
          </Button>
          {hasImages && (
            <Button
              onClick={() => setViewMode(viewMode === 'list' ? 'gallery' : 'list')}
              size="sm"
              variant="outline"
            >
              {viewMode === 'list' ? (
                <>
                  <Grid className="mr-2 h-4 w-4" />
                  Gallery View
                </>
              ) : (
                <>
                  <List className="mr-2 h-4 w-4" />
                  List View
                </>
              )}
            </Button>
          )}
          {selectedForShare.length > 0 && (
            <Button onClick={() => openShareDialog(selectedForShare)} size="sm" variant="outline">
              <Send className="mr-2 h-4 w-4" />
              Share {selectedForShare.length} externally
            </Button>
          )}
          <Button onClick={() => setTextDocDialogOpen(true)} size="sm" variant="outline">
            <Plus className="mr-2 h-4 w-4" />
            Create Text Document
          </Button>
          <Button onClick={() => setUploadDialogOpen(true)} size="sm">
            <FileText className="mr-2 h-4 w-4" />
            Add Document
          </Button>
        </div>
      </div>

      {!documents || documents.length === 0 ? (
        <Card className="p-6 text-center text-muted-foreground">
          <FileText className="mx-auto h-12 w-12 mb-2 opacity-50" />
          <p>No documents uploaded yet</p>
        </Card>
      ) : (
        <>
          {/* Image Gallery View */}
          {hasImages && viewMode === 'gallery' && (
            <Card className="p-6">
              <h4 className="text-md font-semibold mb-4">Images ({imageDocuments.length})</h4>
              <ImageGallery images={imageDocuments} />
            </Card>
          )}

          {/* List View */}
          {viewMode === 'list' && (
            <div className="grid gap-4">
              {documents.map((doc: any) => (
            <Card key={doc.id} className="p-4">
              <div className="flex flex-wrap sm:flex-nowrap items-start justify-between gap-3 sm:gap-4">
                <div className="pt-1">
                  <Checkbox
                    checked={selectedForShare.includes(doc.id)}
                    onCheckedChange={(checked) =>
                      setSelectedForShare((prev) =>
                        checked ? [...new Set([...prev, doc.id])] : prev.filter((x) => x !== doc.id),
                      )
                    }
                    aria-label="Select for external sharing"
                  />
                </div>
                <div className="flex-1 min-w-0 space-y-2">
                  <div className="flex flex-wrap items-center gap-2 break-all">
                    {getFileIcon(doc.file_type, doc.file_name)}
                    {renamingId === doc.id ? (
                      <div className="flex items-center gap-2 flex-1">
                        <Input
                          value={renameValue}
                          onChange={(e) => setRenameValue(e.target.value)}
                          onKeyDown={(e) => {
                            if (e.key === "Enter") saveDisplayName(doc);
                            if (e.key === "Escape") setRenamingId(null);
                          }}
                          placeholder="e.g. Flight ticket — J. Ramudo"
                          className="h-8 max-w-sm text-sm"
                          autoFocus
                        />
                        <Button size="sm" onClick={() => saveDisplayName(doc)}>Save</Button>
                        <Button size="sm" variant="ghost" onClick={() => setRenamingId(null)}>Cancel</Button>
                      </div>
                    ) : (
                      <>
                        <button
                          type="button"
                          className="font-medium text-left hover:underline"
                          title="Click to rename"
                          onClick={() => {
                            setRenamingId(doc.id);
                            setRenameValue(doc.display_name || "");
                          }}
                        >
                          {doc.display_name || doc.file_name}
                        </button>
                        <Button
                          size="sm"
                          variant="ghost"
                          className="h-6 w-6 p-0"
                          onClick={() => {
                            setRenamingId(doc.id);
                            setRenameValue(doc.display_name || "");
                          }}
                        >
                          <Pencil className="h-3 w-3" />
                        </Button>
                        <span className="text-xs px-2 py-1 rounded-full bg-muted text-muted-foreground">
                          {getFileTypeLabel(doc.file_type, doc.file_name)}
                        </span>
                        {doc.shared_with_client && (
                          <Badge className="bg-emerald-600 text-xs">Shared with client</Badge>
                        )}
                        {!!externalShareCounts?.[doc.id] && (
                          <Badge variant="outline" className="text-xs">
                            {externalShareCounts[doc.id]} external share
                            {externalShareCounts[doc.id] === 1 ? "" : "s"}
                          </Badge>
                        )}
                      </>
                    )}
                  </div>
                  {doc.display_name && renamingId !== doc.id && (
                    <p className="text-xs text-muted-foreground">{doc.file_name}</p>
                  )}

                  <div className="flex flex-wrap items-center gap-3 rounded-md border border-dashed p-2">
                    <div className="flex items-center gap-2">
                      <Switch
                        id={`share-${doc.id}`}
                        checked={!!doc.shared_with_client}
                        onCheckedChange={(checked) => toggleShareWithClient(doc, checked)}
                      />
                      <Label htmlFor={`share-${doc.id}`} className="text-xs cursor-pointer">
                        Share with client
                      </Label>
                    </div>
                    {doc.shared_with_client && (
                      <Select
                        value={doc.travel_doc_category || "Other"}
                        onValueChange={(value) => updateTravelCategory(doc, value)}
                      >
                        <SelectTrigger className="h-8 w-[230px] text-xs">
                          <SelectValue placeholder="Category" />
                        </SelectTrigger>
                        <SelectContent>
                          {doc.travel_doc_category &&
                            !TRAVEL_DOC_CATEGORIES.includes(doc.travel_doc_category) && (
                              <SelectItem value={doc.travel_doc_category}>
                                {doc.travel_doc_category} (existing)
                              </SelectItem>
                            )}
                          {TRAVEL_DOC_CATEGORIES.map((cat) => (
                            <SelectItem key={cat} value={cat}>{cat}</SelectItem>
                          ))}
                        </SelectContent>

                      </Select>
                    )}
                  </div>
                  
                  {doc.ai_summary && (
                    <p className="text-sm text-muted-foreground">
                      {doc.ai_summary}
                    </p>
                  )}

                  <div className="flex flex-wrap gap-4 text-xs text-muted-foreground">
                    {doc.extracted_vendor && (
                      <span className="flex items-center gap-1">
                        <FileText className="h-3 w-3" />
                        {doc.extracted_vendor}
                      </span>
                    )}
                    {doc.extracted_document_type && (
                      <span className="capitalize">
                        {doc.extracted_document_type.replace('_', ' ')}
                      </span>
                    )}
                    {doc.extracted_date && (
                      <span className="flex items-center gap-1">
                        <CalendarIcon className="h-3 w-3" />
                        {format(new Date(doc.extracted_date), 'MMM d, yyyy')}
                      </span>
                    )}
                  </div>

                  <div className="flex gap-4 text-xs text-muted-foreground">
                    <span className="flex items-center gap-1">
                      <CalendarIcon className="h-3 w-3" />
                      Uploaded: {format(new Date(doc.uploaded_at), 'MMM d, yyyy')}
                    </span>
                    <span className="flex items-center gap-1">
                      <User className="h-3 w-3" />
                      {doc.profiles?.full_name || doc.profiles?.email || 'Unknown User'}
                    </span>
                  </div>

                  {doc.notes && (
                    <p className="text-sm text-muted-foreground italic">
                      Note: {doc.notes}
                    </p>
                  )}
                </div>

                <div className="flex gap-2">
                  {notesByDocId?.[doc.id] ? (
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => navigate(`/notepad?note=${notesByDocId[doc.id]}`)}
                      title="Open in Notepad"
                    >
                      <StickyNote className="h-4 w-4 mr-1" />
                      Edit Note
                    </Button>
                  ) : (
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => openEditDialog(doc)}
                    >
                      <Pencil className="h-4 w-4" />
                    </Button>
                  )}
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => viewDocument(doc.file_path, doc.file_name, doc.file_type)}
                  >
                    <Eye className="h-4 w-4" />
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => downloadDocument(doc.file_path, doc.file_name)}
                  >
                    <Download className="h-4 w-4" />
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    title="Share externally (guide, hotel, supplier)"
                    onClick={() => openShareDialog([doc.id])}
                  >
                    <Send className="h-4 w-4" />
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    disabled={rescanningId === doc.id}
                    title="Re-scan with AI"
                    onClick={() => rescanDocument(doc)}
                  >
                    <RefreshCw className={`h-4 w-4 ${rescanningId === doc.id ? "animate-spin" : ""}`} />
                  </Button>

                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => deleteDocument(doc.id, doc.file_path)}
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            </Card>
          ))}
            </div>
          )}
        </>
      )}

      <Dialog open={uploadDialogOpen} onOpenChange={setUploadDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Upload Document</DialogTitle>
          </DialogHeader>
          <DocumentUpload
            leadId={leadId}
            itineraryItemId={itineraryItemId}
            onUploadComplete={() => {
              setUploadDialogOpen(false);
              refetch();
            }}
          />
        </DialogContent>
      </Dialog>

      <DocumentViewerDialog
        document={currentDocument}
        open={viewDialogOpen}
        onOpenChange={setViewDialogOpen}
      />

      <ShareDocumentsDialog
        open={shareDialogOpen}
        onOpenChange={(open) => {
          setShareDialogOpen(open);
          if (!open) {
            setSelectedForShare([]);
            queryClient.invalidateQueries({ queryKey: ["document-external-share-counts", leadId] });
          }
        }}
        leadId={leadId}
        documents={(documents || []) as any}
        initialSelectedIds={shareInitialIds}
      />

      <Dialog open={textDocDialogOpen} onOpenChange={setTextDocDialogOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Create Text Document</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label htmlFor="textDocTitle">Document Title</Label>
              <Input
                id="textDocTitle"
                placeholder="e.g., Email from client, WhatsApp notes"
                value={textDocTitle}
                onChange={(e) => setTextDocTitle(e.target.value)}
              />
            </div>
            <div>
              <Label htmlFor="textDocContent">Content</Label>
              <Textarea
                id="textDocContent"
                placeholder="Paste email content, notes, or other text here..."
                value={textDocContent}
                onChange={(e) => setTextDocContent(e.target.value)}
                rows={12}
                className="font-mono text-sm"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setTextDocDialogOpen(false)}>
              Cancel
            </Button>
            <Button onClick={createTextDocument}>
              Create Document
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Edit Document Details</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label htmlFor="editSummary">Summary</Label>
              <Textarea
                id="editSummary"
                placeholder="Document summary..."
                value={editForm.ai_summary}
                onChange={(e) => setEditForm({ ...editForm, ai_summary: e.target.value })}
                rows={4}
              />
            </div>
            <div>
              <Label htmlFor="editVendor">Vendor/Supplier</Label>
              <Select
                value={editForm.extracted_vendor}
                onValueChange={(value) => setEditForm({ ...editForm, extracted_vendor: value })}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select a supplier..." />
                </SelectTrigger>
                <SelectContent>
                  {suppliers?.map((supplier) => (
                    <SelectItem key={supplier.id} value={supplier.name}>
                      {supplier.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label htmlFor="editDocType">Document Type</Label>
              <Select
                value={editForm.extracted_document_type || undefined}
                onValueChange={(value) => setEditForm({ ...editForm, extracted_document_type: value })}
              >
                <SelectTrigger id="editDocType">
                  <SelectValue placeholder="Select a document type..." />
                </SelectTrigger>
                <SelectContent>
                  {editForm.extracted_document_type &&
                    !DOCUMENT_TYPES.includes(editForm.extracted_document_type as any) && (
                      <SelectItem value={editForm.extracted_document_type}>
                        {editForm.extracted_document_type} (existing)
                      </SelectItem>
                    )}
                  {DOCUMENT_TYPES.map((type) => (
                    <SelectItem key={type} value={type}>{type}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <DatePickerField
              label="Date"
              value={editForm.extracted_date ? new Date(editForm.extracted_date) : undefined}
              onChange={(date) => setEditForm({ ...editForm, extracted_date: date ? format(date, "yyyy-MM-dd") : "" })}
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditDialogOpen(false)}>
              Cancel
            </Button>
            <Button onClick={updateDocument}>
              Save Changes
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};
